import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import CommentsThread from "../components/CommentsThread";
import { api } from "../lib/api";
import { fmtMoney, fmtDate } from "../lib/format";
import {
  ClipboardList, Plus, X, Send, Eye, ArrowRight, FileText,
  CheckCircle2, AlertCircle, Clock, Target, ThumbsDown, ThumbsUp, HelpCircle,
} from "lucide-react";

const DECISION_TONE = {
  pending:   "pm-pill-amber",
  approved:  "pm-pill-green",
  rejected:  "pm-pill-red",
  more_info: "pm-pill-blue",
};

const DECISION_ICON = {
  pending: Clock, approved: ThumbsUp, rejected: ThumbsDown, more_info: HelpCircle,
};

export default function ScoutingHome() {
  const [data, setData] = useState(null);
  const [submitBrief, setSubmitBrief] = useState(null); // brief obj or null
  const load = () => api.get("/scouting/home").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) {
    return (
      <Layout title="Scouting suite" subtitle="Briefs from the Director · candidate submissions">
        <div className="text-slate-500" data-testid="scouting-loading">Loading scouting workspace…</div>
      </Layout>
    );
  }
  const { briefs, submissions, stats } = data;

  return (
    <Layout title="Scouting suite" subtitle={`${data.user.name} · ${data.user.role.replace("_", " ")}`}>
      <div className="space-y-6" data-testid="scouting-home-root">
        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat tone="amber" label="Open briefs"      value={stats.open_briefs}      icon={ClipboardList} testid="stat-open-briefs" />
          <Stat tone="blue"  label="Pending decisions" value={stats.pending_decisions} icon={Clock}        testid="stat-pending" />
          <Stat tone="green" label="Approved"          value={stats.approved}          icon={CheckCircle2} testid="stat-approved" />
          <Stat tone="red"   label="Rejected"          value={stats.rejected}          icon={ThumbsDown}   testid="stat-rejected" />
        </div>

        {/* Open briefs */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">From the Tech Director</div>
              <h2 className="font-display text-xl font-black tracking-tight flex items-center gap-2"><ClipboardList size={18} /> Open scout briefs</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="briefs-list">
            {briefs.length === 0 && <div className="pm-card p-6 text-center text-sm text-slate-500" data-testid="briefs-empty">No briefs yet.</div>}
            {briefs.map((b) => (
              <div key={b.id} className="pm-card p-4 border-l-4" style={{ borderLeftColor: b.status === "open" ? "#f59e0b" : b.status === "in_progress" ? "#2563eb" : "#10b981" }} data-testid={`brief-${b.id}`}>
                <div className="flex items-start gap-2 mb-1">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-display font-black text-sm shrink-0">{b.slot_label}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-base">{b.position}</div>
                    <div className="text-[11px] text-slate-500">Requested by {b.requested_by} · {b.target_window || "—"}</div>
                  </div>
                  <span className={`pm-pill text-[10px] ${b.status === "open" ? "pm-pill-amber" : b.status === "in_progress" ? "pm-pill-blue" : "pm-pill-green"}`}>{b.status.replace("_", " ")}</span>
                </div>
                <div className="text-xs text-slate-600 leading-relaxed mt-2 italic">"{b.profile_criteria}"</div>
                <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
                  <span className="font-mono-tab">Budget {fmtMoney(b.budget_ceiling_eur)}</span>
                  <span>·</span>
                  <span>{b.submissions_count} submission{b.submissions_count !== 1 ? "s" : ""}</span>
                  {b.assigned_to && <><span>·</span><span>Assigned to {b.assigned_to}</span></>}
                  <button
                    onClick={() => setSubmitBrief(b)}
                    className="ml-auto pm-btn-primary h-8 px-3 rounded-lg text-[11px] font-bold flex items-center gap-1"
                    data-testid={`submit-cand-btn-${b.id}`}
                  >
                    <Plus size={11} /> Submit candidate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* My submissions */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">My pipeline</div>
              <h2 className="font-display text-xl font-black tracking-tight flex items-center gap-2"><FileText size={18} /> Submissions ({submissions.length})</h2>
            </div>
          </div>
          <div className="pm-card divide-y divide-slate-100" data-testid="submissions-list">
            {submissions.length === 0 && <div className="p-6 text-center text-sm text-slate-500" data-testid="submissions-empty">No submissions yet.</div>}
            {submissions.map((s) => {
              const Icon = DECISION_ICON[s.decision];
              return (
                <div key={s.id} className="p-4 flex items-start gap-3" data-testid={`sub-${s.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                    <Icon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold truncate">{s.player_name}</span>
                      <span className="pm-pill pm-pill-slate text-[10px]">{s.position}</span>
                      <span className={`pm-pill text-[10px] ${DECISION_TONE[s.decision]}`}>{s.decision.replace("_", " ")}</span>
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">{s.age ? `${s.age}y · ` : ""}{s.nationality} · {s.current_club} · {fmtMoney(s.recommended_fee_eur)}</div>
                    <div className="text-[11px] text-slate-600 mt-1 italic line-clamp-2">"{s.notes}"</div>
                    {s.decision_note && (
                      <div className="text-[11px] text-emerald-700 mt-1.5 flex items-start gap-1">
                        <Send size={10} className="mt-0.5 shrink-0" /> TD: {s.decision_note}
                      </div>
                    )}
                    <CommentsThread recordType="submission" recordId={s.id} testid={`comments-sub-${s.id}`} />
                  </div>
                  <span className="font-mono-tab text-[10px] text-slate-400 shrink-0">{fmtDate(s.created_at)}</span>
                </div>
              );
            })}
          </div>
        </section>

        <div className="pm-card p-4 flex items-start gap-3 border-l-4 border-l-emerald-400 bg-emerald-50/30">
          <AlertCircle size={14} className="text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-xs text-slate-700">
            Your account is scoped to the <b>scouting suite</b>. You see open briefs from the Director, your submissions and their decisions, and the position map. Player wages, contracts, agent financials, and medical confidentials are not visible to your role.
          </div>
        </div>
      </div>

      {submitBrief && (
        <SubmitCandidateModal
          brief={submitBrief}
          onClose={() => setSubmitBrief(null)}
          onSaved={() => { setSubmitBrief(null); load(); }}
        />
      )}
    </Layout>
  );
}

function Stat({ tone, label, value, icon: Icon, testid }) {
  const cls = tone === "amber" ? "border-amber-200 bg-amber-50"
    : tone === "blue" ? "border-blue-200 bg-blue-50"
    : tone === "green" ? "border-emerald-200 bg-emerald-50"
    : tone === "red" ? "border-red-200 bg-red-50"
    : "border-slate-200 bg-slate-50";
  return (
    <div className={`pm-card border ${cls} p-4`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-slate-600" />
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</div>
      </div>
      <div className="font-display text-3xl font-black tracking-tight mt-1">{value}</div>
    </div>
  );
}

function SubmitCandidateModal({ brief, onClose, onSaved }) {
  const [form, setForm] = useState({
    player_name: "", position: brief.slot_label, age: "", nationality: "",
    current_club: "", recommended_fee_eur: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.player_name.trim() && form.notes.trim();

  const submit = async () => {
    if (!valid) return;
    setBusy(true); setErr(null);
    try {
      await api.post("/scouting/submissions", {
        brief_id: brief.id,
        player_name: form.player_name.trim(),
        position: form.position || brief.slot_label,
        age: form.age ? Number(form.age) : null,
        nationality: form.nationality || null,
        current_club: form.current_club || null,
        recommended_fee_eur: form.recommended_fee_eur ? Number(form.recommended_fee_eur) : null,
        notes: form.notes.trim(),
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="submit-cand-modal">
      <div className="bg-white rounded-2xl w-full max-w-xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Submit candidate</div>
            <h3 className="font-display text-xl font-black tracking-tight">{brief.slot_label} · {brief.position}</h3>
            <div className="text-xs text-slate-500 mt-0.5 italic">"{brief.profile_criteria}"</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="submit-cand-close"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Player name *">
            <input className="pm-input" value={form.player_name} onChange={(e) => setF("player_name", e.target.value)} data-testid="cand-name" />
          </Field>
          <Field label="Position">
            <input className="pm-input" value={form.position} onChange={(e) => setF("position", e.target.value)} />
          </Field>
          <Field label="Age">
            <input type="number" className="pm-input" value={form.age} onChange={(e) => setF("age", e.target.value)} />
          </Field>
          <Field label="Nationality">
            <input className="pm-input" value={form.nationality} onChange={(e) => setF("nationality", e.target.value)} />
          </Field>
          <Field label="Current club" wide>
            <input className="pm-input" value={form.current_club} onChange={(e) => setF("current_club", e.target.value)} />
          </Field>
          <Field label="Recommended fee (€)" wide>
            <input type="number" className="pm-input" value={form.recommended_fee_eur} onChange={(e) => setF("recommended_fee_eur", e.target.value)} />
          </Field>
          <Field label="Scouting notes *" wide>
            <textarea className="pm-input" rows={4} value={form.notes} onChange={(e) => setF("notes", e.target.value)} data-testid="cand-notes" placeholder="Why this player matches the brief…" />
          </Field>
        </div>
        {err && <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{String(err)}</div>}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold" onClick={onClose}>Cancel</button>
          <button
            className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold disabled:opacity-50"
            disabled={!valid || busy}
            onClick={submit}
            data-testid="cand-submit-btn"
          >
            {busy ? "Submitting…" : "Submit to Director"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
